import React from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { DevAutofillButton } from '../BuzzBridge/DevAutofillButton';
import { getDevBrandManualSocialLinkData } from '../../utils/devAutofill';
import {
    brandManualSocialUrlFromHandle,
    normalizeBrandManualHandle,
    type IBrandManualSocialLinkData,
} from '../../utils/brandManualSocialLinkUtils';
import type { ISocialLinks } from '../../redux/buzzbridge/types';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER_HIGH } from '../../pages/BrandDashboard/brandDashboardTokens';

const fieldLabelSx = {
    fontSize: 10,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.14em',
    fontWeight: 700,
    mb: 0.75,
    color: '#adaaaa',
    fontFamily: FONT_ENUM.MAIN_BOLD,
};

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
        borderRadius: 2,
        color: '#fff',
        fontSize: 14,
        '& fieldset': { border: 'none' },
        '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(236,170,0,0.35)' },
    },
};

interface Props {
    platformId: keyof ISocialLinks;
    platformLabel: string;
    value: IBrandManualSocialLinkData;
    onChange: (data: IBrandManualSocialLinkData) => void;
}

export function BrandManualSocialLinkForm({ platformId, platformLabel, value, onChange }: Props) {
    const patch = (field: keyof IBrandManualSocialLinkData, raw: string) => {
        onChange({ ...value, [field]: raw });
    };

    const handle = normalizeBrandManualHandle(value.username);
    const previewUrl = brandManualSocialUrlFromHandle(platformId as 'instagram' | 'tiktok' | 'youtube', handle);

    const onHandleChange = (raw: string) => {
        const username = normalizeBrandManualHandle(raw);
        onChange({
            ...value,
            username,
            url: brandManualSocialUrlFromHandle(platformId as 'instagram' | 'tiktok' | 'youtube', username),
        });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Alert
                    severity="info"
                    sx={{
                        flex: 1,
                        borderRadius: 2,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 12,
                        '& .MuiAlert-message': { width: '100%' },
                    }}>
                    Enter your {platformLabel} handle — we&apos;ll build the profile URL for you. Manual links are
                    stored with <strong>linked: false</strong> (not OAuth).
                </Alert>
                <DevAutofillButton
                    title={`Dev: fill ${platformLabel} manual link`}
                    color={BRAND_LUXE_PRIMARY}
                    onAutofill={() =>
                        onChange(getDevBrandManualSocialLinkData(platformId as 'instagram' | 'tiktok' | 'youtube'))
                    }
                />
            </Box>

            <Box>
                <Typography sx={fieldLabelSx}>
                    Handle{' '}
                    <Typography component="span" sx={{ color: '#f87171' }}>
                        *
                    </Typography>
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="@yourbrand"
                    value={value.username}
                    onChange={e => onHandleChange(e.target.value)}
                    sx={textFieldSx}
                />
                {previewUrl ? (
                    <Typography sx={{ mt: 0.75, fontSize: 11, color: '#767575' }}>
                        Profile URL:{' '}
                        <Typography component="span" sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 11 }}>
                            {previewUrl}
                        </Typography>
                    </Typography>
                ) : null}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Box>
                    <Typography sx={fieldLabelSx}>Followers</Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="10000"
                        value={value.follower_count}
                        onChange={e => patch('follower_count', e.target.value.replace(/[^\d]/g, ''))}
                        inputProps={{ inputMode: 'numeric' }}
                        sx={textFieldSx}
                    />
                </Box>
                <Box>
                    <Typography sx={fieldLabelSx}>Engagement rate (%)</Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="3.5"
                        value={value.engagement_rate}
                        onChange={e => patch('engagement_rate', e.target.value.replace(/[^\d.]/g, ''))}
                        inputProps={{ inputMode: 'decimal' }}
                        sx={textFieldSx}
                    />
                </Box>
            </Box>

            <Typography sx={{ fontSize: 11, color: '#767575', lineHeight: 1.5 }}>
                Manual links are saved with <strong style={{ color: BRAND_LUXE_PRIMARY }}>linked: false</strong> so
                brands can verify your profile before campaigns go live.
            </Typography>
        </Box>
    );
}
